import { UploadChunkToSessionRequest, CPS_NOTIFICATION_TYPE } from '@cs-cms/types';
import { FileHelper } from '@cs-cms/utils';
import { useMutation, UseMutationResult } from '@tanstack/react-query';
import { useContext } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useGlobalizationContext } from '../../contexts/GlobalizationContext';
import ServicesContext from '../../contexts/ServicesContext';
import { mutateDocumentsKeys } from '../../mutationKeys/documents';
import { useTranslation } from '../globalization/useTranslation';
import { useNotificationContext } from '../useNotificationContext';

export function useUploadChunk(): UseMutationResult<
  void,
  unknown,
  { dto: UploadChunkToSessionRequest; totalChunks: number }
> {
  const servicesCtx = useContext(ServicesContext);
  const { updateCenterNotification, showToast } = useNotificationContext();
  const { selectedLocale } = useGlobalizationContext();
  const { translate } = useTranslation(selectedLocale);

  const uploadChunk = (data: { dto: UploadChunkToSessionRequest }): Promise<void> => {
    if (servicesCtx?.documentsService && data.dto) {
      return servicesCtx.documentsService?.uploadChunk(data.dto);
    }
    throw new Error('Services context not initialized');
  };

  return useMutation({
    mutationKey: mutateDocumentsKeys.uploadChunk,
    mutationFn: (data: { dto: UploadChunkToSessionRequest; totalChunks: number }) => uploadChunk(data),
    onSuccess: (result, variables) => {
      const fileName = variables.dto.chunk.file.name;
      const fileSize = variables.dto.chunk.file.size;
      const notificationId = FileHelper.getFileNotificationId(fileName);

      const chunkIndex = variables.dto.chunk.chunkIndex;
      const totalChunks = variables.totalChunks;
      const percent = Math.min(Math.round(((chunkIndex + 1) / totalChunks) * 100), 99);
      const bytesUploaded = Math.min(Math.round(((chunkIndex + 1) / totalChunks) * fileSize), fileSize - 1);

      updateCenterNotification(notificationId, {
        message: translate('custom.notifications.notificationCenter.uploadDocument.uploadDocumentInProgress'),
        progress: {
          current: bytesUploaded,
          total: totalChunks,
          percent
        }
      });
    },
    onError: (data, variables) => {
      showToast({
        type: CPS_NOTIFICATION_TYPE.ERROR,
        message: translate('custom.notifications.notificationCenter.uploadDocument.uploadDocumentFailedTitle')
      });

      const fileName = variables.dto.chunk.file.name;
      const notificationId = FileHelper.getFileNotificationId(fileName);

      updateCenterNotification(notificationId, {
        id: uuidv4(),
        title: translate('custom.notifications.notificationCenter.uploadDocument.uploadDocumentFailedTitle'),
        message: translate('custom.notifications.notificationCenter.uploadDocument.uploadDocumentFailedMessage', {
          fileName: variables.dto.chunk.file.name
        }),
        timestamp: new Date(),
        type: 'error'
      });
    }
  });
}
